/* === solara-form.jsx === */
// Solara Living shared form runtime.
//
// Provides three things to every page that has a lead form:
//   - WizardForm        : the multi-step wizard used by the home page + 4 product pages
//   - FileUploadField   : <input type=file> with chips, validation, and visible state
//   - CommentUploadsStep: the final "anything we should see?" step that replaces
//                         the legacy time-slot picker (per session 1b spec)
//   - submitLead        : multipart POST helper that targets /api/submit.php
//   - useFormSession    : session_id + debounced /api/track.php tracking
//
// Loaded BEFORE any page-specific jsx so the page jsx can render <WizardForm/>
// without redefining wizard mechanics in five places.
//
// File limits are enforced both client- and server-side. The server source of
// truth is api/config.example.php (uploads.max_bytes / .max_files). Keep these
// in sync when bumping caps.

// --- CMS content lookup helpers (top-level so every page that loads this file
// can access them via bare `_C` / `_CA`). On the home page they're shadowed
// (redeclared) by the copies in solara-sections.jsx — harmless, second one
// wins. about-page.jsx defines its own copies because it does not load this
// file.
function _C(path, fallback){
  const root = (typeof window !== "undefined" && window.__SOLARA_CONTENT__) || (typeof globalThis !== "undefined" && globalThis.__SOLARA_CONTENT__) || null;
  if (!root) return fallback;
  const v = String(path).split('.').reduce((o, k) => (o == null ? o : o[k]), root);
  return (v === undefined || v === null || v === '') ? fallback : v;
}
function _CA(path, fallback){
  const v = _C(path, null);
  return Array.isArray(v) && v.length ? v : fallback;
}

(() => {
  const { useState, useEffect, useRef, useMemo, useCallback } = React;

  // --- Inject file-upload CSS once -------------------------------------------
  // The form-card/.field/.opts styles already live inline in every page HTML.
  // We only need to add the new classes introduced by FileUploadField, so we
  // inject a <style> tag the first time solara-form.jsx runs.
  if (typeof document !== 'undefined' && !document.getElementById('solara-form-css')) {
    const style = document.createElement('style');
    style.id = 'solara-form-css';
    style.textContent = `
      .field-files .files-help {
        font-size: 13px; color: var(--muted, #6b6258); margin: 4px 0 10px;
      }
      .field-files .files-list {
        display: flex; flex-wrap: wrap; gap: 8px; align-items: stretch;
      }
      .field-files .file-chip {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 8px 10px 8px 14px;
        background: var(--bg-2, #efe7da); border-radius: 999px;
        font-size: 14px; max-width: 100%; color: var(--text, #2b2620);
      }
      .field-files .file-chip-name {
        max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .field-files .file-chip-size {
        font-size: 12px; color: var(--muted, #6b6258);
      }
      .field-files .file-chip-remove {
        appearance: none; border: 0; background: transparent;
        width: 22px; height: 22px; border-radius: 50%;
        font-size: 17px; line-height: 1; color: var(--muted, #6b6258);
        cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
        transition: background 120ms ease, color 120ms ease;
      }
      .field-files .file-chip-remove:hover {
        background: rgba(0,0,0,0.08); color: var(--text, #2b2620);
      }
      .field-files .file-add {
        appearance: none;
        display: inline-flex; align-items: center; gap: 8px;
        padding: 9px 14px;
        background: transparent;
        border: 1.5px dashed var(--border, #c9bda7);
        border-radius: 999px;
        font-size: 14px; color: var(--text, #2b2620);
        cursor: pointer;
        transition: border-color 120ms ease, background 120ms ease;
      }
      .field-files .file-add:hover {
        border-color: var(--text, #2b2620);
        background: rgba(0,0,0,0.03);
      }
      .field-files .file-add-plus {
        font-size: 16px; line-height: 1; color: var(--muted, #6b6258);
      }
    `;
    document.head.appendChild(style);
  }

  // --- Constants (mirror api/config.example.php) -----------------------------
  const FORM_MAX_BYTES = 8 * 1024 * 1024; // 8 MB per file
  const FORM_MAX_FILES = 3;
  const FORM_ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'pdf'];
  const FORM_ALLOWED_ACCEPT = '.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

  // --- uuid v4 (no crypto.randomUUID for old Safari) -------------------------
  function uuidV4() {
    const bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  function sessionKey(formId) {
    return `solara_form_session_${formId || 'main'}`;
  }

  function loadSession(formId) {
    try {
      const v = localStorage.getItem(sessionKey(formId));
      if (v && /^[0-9a-f-]{36}$/i.test(v)) return v;
    } catch (e) { /* localStorage unavailable */ }
    const fresh = uuidV4();
    try { localStorage.setItem(sessionKey(formId), fresh); } catch (e) {}
    return fresh;
  }

  function clearSession(formId) {
    try { localStorage.removeItem(sessionKey(formId)); } catch (e) {}
  }

  // --- UTM / referrer capture (one-time, persisted) --------------------------
  // Parsed once per page load. Used as request fields on every track + submit
  // call so the lead row gets first-touch attribution.
  function readAttribution() {
    const url = new URL(location.href);
    const q = url.searchParams;
    return {
      page_url:     location.href,
      referrer:     document.referrer || '',
      utm_source:   q.get('utm_source')   || '',
      utm_medium:   q.get('utm_medium')   || '',
      utm_campaign: q.get('utm_campaign') || '',
      utm_content:  q.get('utm_content')  || '',
      utm_term:     q.get('utm_term')     || '',
    };
  }

  // --- useFormSession --------------------------------------------------------
  // Returns { sessionId, track, formStartedAt, secondsOnForm } for a given
  // formId. `track` is debounced ~600ms so typing doesn't flood the endpoint;
  // step transitions call track() and the debounce coalesces into one POST.
  function useFormSession(formId) {
    const sessionIdRef = useRef(null);
    const startedAtRef = useRef(null);
    const timerRef = useRef(null);
    const attribution = useMemo(() => readAttribution(), []);

    if (sessionIdRef.current === null) sessionIdRef.current = loadSession(formId);
    if (startedAtRef.current === null) startedAtRef.current = Date.now();

    const track = useCallback((body) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const sessionId = sessionIdRef.current;
      const startedAt = startedAtRef.current;
      timerRef.current = setTimeout(() => {
        const time_on_form = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
        fetch('/api/track.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            session_id: sessionId,
            form_id: formId,
            time_on_form,
            ...attribution,
            ...body,
          }),
        }).catch(() => { /* tracking is best-effort; swallow errors */ });
      }, 600);
    }, [formId, attribution]);

    return {
      sessionId: sessionIdRef.current,
      track,
      attribution,
      startedAt: startedAtRef.current,
    };
  }

  // --- submitLead ------------------------------------------------------------
  // Multipart POST to /api/submit.php. files is an array of File objects.
  // Returns { ok: true, lead_id, email_sent } on success, throws on failure.
  async function submitLead(args) {
    const { sessionId, formId, productInterest, fields, files, attribution, startedAt, lastField, stepReached } = args;
    const fd = new FormData();
    fd.append('session_id', sessionId);
    fd.append('form_id', formId);
    fd.append('product_interest', productInterest || '');
    fd.append('page_url', attribution.page_url);
    fd.append('referrer', attribution.referrer);
    fd.append('utm_source', attribution.utm_source);
    fd.append('utm_medium', attribution.utm_medium);
    fd.append('utm_campaign', attribution.utm_campaign);
    fd.append('utm_content', attribution.utm_content);
    fd.append('utm_term', attribution.utm_term);
    fd.append('last_field', lastField || '');
    fd.append('step_reached', String(stepReached || 0));
    fd.append('time_on_form', String(Math.max(0, Math.round((Date.now() - startedAt) / 1000))));
    fd.append('name', fields.name || '');
    fd.append('email', fields.email || '');
    fd.append('phone', fields.phone || '');
    fd.append('message', fields.message || '');
    fd.append('fields_data', JSON.stringify(fields.fields_data || {}));
    fd.append('fields_touched', JSON.stringify(fields.fields_touched || []));

    (files || []).forEach((f) => fd.append('files[]', f));

    const r = await fetch('/api/submit.php', {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
    });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j || j.ok !== true) {
      const msg = (j && j.error) || `Submit failed (${r.status})`;
      throw new Error(msg);
    }
    return j;
  }

  // --- File helpers ----------------------------------------------------------
  function fileExtOk(name) {
    const ext = (name.split('.').pop() || '').toLowerCase();
    return FORM_ALLOWED_EXT.includes(ext);
  }
  function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }
  function validateFile(file, existingCount) {
    if (existingCount >= FORM_MAX_FILES) {
      return `Max ${FORM_MAX_FILES} files.`;
    }
    if (!fileExtOk(file.name)) {
      return `Only JPG, PNG, WebP, HEIC, or PDF.`;
    }
    if (file.size > FORM_MAX_BYTES) {
      return `Each file must be under ${Math.round(FORM_MAX_BYTES / 1024 / 1024)} MB. "${file.name}" is ${formatBytes(file.size)}.`;
    }
    return null;
  }

  // --- FileUploadField -------------------------------------------------------
  // Controlled component. `files` is an array of File; `setFiles` replaces it.
  // Shows a "+ Add files" trigger, accepted chips with × remove, and an inline
  // error line when a file is rejected.
  function FileUploadField({ files, setFiles, error }) {
    const inputRef = useRef(null);
    const [localErr, setLocalErr] = useState('');

    const onPick = (e) => {
      const picked = Array.from(e.target.files || []);
      const next = [...files];
      let firstErr = '';
      for (const f of picked) {
        const err = validateFile(f, next.length);
        if (err) { firstErr = firstErr || err; continue; }
        // Dedupe by name+size (best-effort).
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      setFiles(next);
      setLocalErr(firstErr);
      // Reset the input so re-picking the same file fires onChange again.
      e.target.value = '';
    };

    const onRemove = (i) => {
      const next = files.slice();
      next.splice(i, 1);
      setFiles(next);
      setLocalErr('');
    };

    const remaining = FORM_MAX_FILES - files.length;
    const trigger = () => inputRef.current && inputRef.current.click();
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); trigger(); }
    };

    return (
      <div className="field field-files">
        <label>Photos or floor plans (optional)</label>
        <div className="files-help">
          Up to {FORM_MAX_FILES} files, {Math.round(FORM_MAX_BYTES / 1024 / 1024)} MB each. JPG, PNG, WebP, HEIC, or PDF.
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={FORM_ALLOWED_ACCEPT}
          multiple
          onChange={onPick}
          style={{position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0}}
          aria-hidden="true"
          tabIndex={-1}
        />

        <div className="files-list">
          {files.map((f, i) => (
            <div className="file-chip" key={`${f.name}-${f.size}-${i}`}>
              <span className="file-chip-name" title={f.name}>{f.name}</span>
              <span className="file-chip-size">{formatBytes(f.size)}</span>
              <button
                type="button"
                className="file-chip-remove"
                aria-label={`Remove ${f.name}`}
                onClick={() => onRemove(i)}
              >×</button>
            </div>
          ))}
          {remaining > 0 && (
            <button
              type="button"
              className="file-add"
              onClick={trigger}
              onKeyDown={onKey}
            >
              <span className="file-add-plus" aria-hidden="true">+</span>
              <span>{files.length === 0 ? 'Add files' : `Add another (${remaining} left)`}</span>
            </button>
          )}
        </div>

        {(localErr || error) && <div className="err-msg">{localErr || error}</div>}
      </div>
    );
  }

  // --- CommentUploadsStep ----------------------------------------------------
  // The "anything we should see?" step. Replaces the legacy slot picker.
  // Used by every WizardForm via getCommentUploadsStep().
  function CommentUploadsStep({ data, update, errors }) {
    return (
      <div className="field-stack">
        <div className="field">
          <label>Anything we should know? (optional)</label>
          <textarea
            rows={5}
            placeholder="Site quirks, ideas, photos coming separately, deadlines — anything that helps us prep."
            value={data.message || ''}
            onChange={(e) => update('message', e.target.value)}
          />
        </div>
        <FileUploadField
          files={data.files || []}
          setFiles={(next) => update('files', next)}
          error={errors.files}
        />
      </div>
    );
  }

  // --- Shared step renderers (used by every wizard form) --------------------
  // StepPostal: postal code + property type. Validation: Canadian postal.
  function StepPostal({ data, update, errors }) {
    return (
      <div className="field-stack">
        <div className="field">
          <label>Postal code</label>
          <input
            placeholder="M5V 2T6"
            value={data.postal || ''}
            onChange={(e) => update('postal', e.target.value)}
            className={errors.postal ? 'err' : ''}
          />
          {errors.postal && <div className="err-msg">{errors.postal}</div>}
        </div>
        <div className="field">
          <label>Property type</label>
          <div className="seg">
            {['Residential', 'Commercial'].map((v) => (
              <button
                key={v}
                type="button"
                className={data.venue === v ? 'on' : ''}
                onClick={() => update('venue', v)}
              >{v}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // StepWhen: timing dropdown buttons.
  function StepWhen({ data, update, errors }) {
    return (
      <div className="opts">
        {['1–3 months', '3–6 months', '6–12 months', 'Just researching'].map((v) => (
          <button
            key={v}
            type="button"
            className={`opt ${data.when === v ? 'sel' : ''}`}
            onClick={() => update('when', v)}
          >
            <span>{v}</span>
            <span className="dot" />
          </button>
        ))}
        {errors.when && <div className="err-msg">{errors.when}</div>}
      </div>
    );
  }

  // StepContact: name + email + phone + preferred contact channel.
  function StepContact({ data, update, errors }) {
    return (
      <div className="field-stack">
        <div className="field">
          <label>Name</label>
          <input
            placeholder="Jane Tremblay"
            value={data.name || ''}
            onChange={(e) => update('name', e.target.value)}
            className={errors.name ? 'err' : ''}
          />
          {errors.name && <div className="err-msg">{errors.name}</div>}
        </div>
        <div className="field-row">
          <div className="field">
            <label>Email</label>
            <input
              placeholder="jane@email.com"
              value={data.email || ''}
              onChange={(e) => update('email', e.target.value)}
              className={errors.email ? 'err' : ''}
            />
            {errors.email && <div className="err-msg">{errors.email}</div>}
          </div>
          <div className="field">
            <label>Phone</label>
            <input
              placeholder="416 555 0123"
              value={data.phone || ''}
              onChange={(e) => update('phone', e.target.value)}
              className={errors.phone ? 'err' : ''}
            />
            {errors.phone && <div className="err-msg">{errors.phone}</div>}
          </div>
        </div>
        <div className="field">
          <label>Preferred contact</label>
          <div className="seg">
            {['Phone', 'Email', 'Text'].map((v) => (
              <button
                key={v}
                type="button"
                className={data.contactPref === v ? 'on' : ''}
                onClick={() => update('contactPref', v)}
              >{v}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Standard validators for the shared steps. Each form composes them into its
  // step list via the validate field; centralising them here keeps the rules
  // identical across the 5 wizard forms.
  function validatePostal(d) {
    return isCanadianPostalCode(d.postal) ? {} : { postal: 'Enter a valid Canadian postal code.' };
  }
  function validateWhen(d) {
    return d.when ? {} : { when: 'Pick a timing.' };
  }
  function validateContact(d) {
    const e = {};
    if (!String(d.name || '').trim()) e.name = 'Your name, please.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email || '')) e.email = 'Valid email, please.';
    if (!/^[\d\s\-+()]{10,}$/.test(d.phone || '')) e.phone = 'Valid phone, please.';
    return e;
  }

  function getCommentUploadsStep(opts) {
    const o = opts || {};
    return {
      id: 'uploads',
      title: o.title || 'Anything we should see?',
      sub:   o.sub   || "Drop a few photos of your space if you have them. Or just hit send — we'll talk it through on the call.",
      render: CommentUploadsStep,
      // No validation: comment + uploads are both optional.
      validate: () => ({}),
    };
  }

  // --- WizardForm ------------------------------------------------------------
  // Drives the form-card UI: progress bar, step transitions, validation, the
  // final "we'll be in touch within one business day" confirmation card.
  //
  // Props
  // - formId         : "main" | "pergolas" | "saunas" | "hottubs" | "swimspas"
  // - sectionId      : DOM id for the wrapping <section> (default "book")
  // - eyebrow        : top tag above the title (e.g. "Pergola consultation")
  // - title          : h2 text (e.g. "Two minutes. No pressure.")
  // - body           : paragraph under the title
  // - initialData    : initial form state
  // - steps          : array of { id, title, sub?, render, validate }
  //                    render({data, update, errors, toggleArr}) returns JSX.
  //                    validate(data) returns {field: msg} or {}.
  // - productInterest: string OR (data)=>string. Used on submit + tracking.
  // - confirmMeta    : (data)=>[{label, value}, ...] for the confirmation card.
  function WizardForm({
    formId,
    sectionId,
    eyebrow,
    title,
    body,
    initialData,
    steps,
    productInterest,
    confirmMeta,
  }) {
    const allSteps = useMemo(() => steps.concat([{ id: 'done', title: "We've got you.", sub: '', isConfirm: true }]), [steps]);
    const [step, setStep] = useState(0);
    const [data, setData] = useState(() => ({
      message: '',
      files: [],
      fields_touched: [],
      ...(initialData || {}),
    }));
    const [errors, setErrors] = useState({});
    const [direction, setDirection] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const session = useFormSession(formId);

    const update = useCallback((k, v) => {
      setData((d) => {
        const touched = d.fields_touched && d.fields_touched.includes(k) ? d.fields_touched : [...(d.fields_touched || []), k];
        const next = { ...d, [k]: v, fields_touched: touched };
        // Track on each user edit, debounced.
        const productInt = typeof productInterest === 'function' ? productInterest(next) : productInterest;
        session.track({
          name: next.name || '',
          email: next.email || '',
          phone: next.phone || '',
          product_interest: productInt || '',
          message: next.message || '',
          fields_data: serializeFields(next),
          fields_touched: next.fields_touched,
          last_field: k,
          step_reached: step,
        });
        return next;
      });
    }, [productInterest, session, step]);

    const toggleArr = useCallback((k, v) => {
      setData((d) => {
        const arr = (d[k] || []).includes(v)
          ? (d[k] || []).filter((x) => x !== v)
          : [...(d[k] || []), v];
        const touched = d.fields_touched && d.fields_touched.includes(k) ? d.fields_touched : [...(d.fields_touched || []), k];
        const next = { ...d, [k]: arr, fields_touched: touched };
        const productInt = typeof productInterest === 'function' ? productInterest(next) : productInterest;
        session.track({
          name: next.name || '',
          email: next.email || '',
          phone: next.phone || '',
          product_interest: productInt || '',
          message: next.message || '',
          fields_data: serializeFields(next),
          fields_touched: next.fields_touched,
          last_field: k,
          step_reached: step,
        });
        return next;
      });
    }, [productInterest, session, step]);

    const validateStep = useCallback((i, d) => {
      const s = steps[i];
      if (!s || !s.validate) return {};
      const e = s.validate(d) || {};
      setErrors(e);
      return e;
    }, [steps]);

    const next = useCallback(() => {
      const e = validateStep(step, data);
      if (Object.keys(e).length > 0) return;
      setDirection(1);
      setStep((s) => Math.min(allSteps.length - 1, s + 1));
    }, [step, data, validateStep, allSteps.length]);

    const back = useCallback(() => {
      setDirection(-1);
      setStep((s) => Math.max(0, s - 1));
    }, []);

    const total = steps.length; // confirm step is index === total
    const onBookable = step < total;
    const isLastInput = step === total - 1;
    const progress = onBookable ? step / total : 1;

    const doSubmit = useCallback(async () => {
      // Validate the final input step before submitting.
      const e = validateStep(step, data);
      if (Object.keys(e).length > 0) return;
      setSubmitting(true);
      setSubmitError('');
      try {
        const productInt = typeof productInterest === 'function' ? productInterest(data) : productInterest;
        await submitLead({
          sessionId: session.sessionId,
          formId,
          productInterest: productInt,
          fields: {
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            message: data.message || '',
            fields_data: serializeFields(data),
            fields_touched: data.fields_touched || [],
          },
          files: data.files || [],
          attribution: session.attribution,
          startedAt: session.startedAt,
          lastField: 'submit',
          stepReached: step + 1,
        });
        // On success, clear the session so the next visit starts fresh.
        clearSession(formId);
        setSubmitted(true);
        setDirection(1);
        setStep(allSteps.length - 1);
      } catch (err) {
        setSubmitError(err && err.message ? err.message : 'Could not submit. Please try again or call us.');
      } finally {
        setSubmitting(false);
      }
    }, [data, step, validateStep, session, formId, productInterest, allSteps.length]);

    const current = allSteps[step];
    const isConfirm = current && current.isConfirm;
    const StepRender = !isConfirm && steps[step] && steps[step].render;

    return (
      <section className="form-section" id={sectionId || 'book'}>
        <div className="wrap">
          <div className="form-head">
            <span className="eyebrow">{eyebrow}</span>
            <h2 className="h2">{title}</h2>
            {body && <p className="body-lg">{body}</p>}
          </div>

          <div className="form-card">
            <div className="form-progress">
              <div className="form-progress-bar" style={{ width: `${progress * 100}%` }} />
              <div className="form-progress-meta">
                <span>{onBookable ? `Step ${step + 1} of ${total}` : 'Sent'}</span>
                {step > 0 && onBookable && (
                  <button type="button" className="back-btn" onClick={back}>← Back</button>
                )}
              </div>
            </div>

            <div className="form-stage">
              <div key={step} className={`step ${direction > 0 ? 'fwd' : 'back'}`}>
                <h3 className="step-title">{current ? current.title : ''}</h3>
                {current && current.sub && <p className="step-sub">{current.sub}</p>}

                {!isConfirm && StepRender && (
                  <StepRender
                    data={data}
                    update={update}
                    toggleArr={toggleArr}
                    errors={errors}
                  />
                )}

                {isConfirm && submitted && (
                  <ConfirmCard data={data} confirmMeta={confirmMeta} />
                )}

                {submitError && (
                  <div className="err-msg" style={{ marginTop: 12 }}>{submitError}</div>
                )}
              </div>
            </div>

            {onBookable && (
              <div className="form-foot">
                <div className="form-foot-meta">
                  {/* Per-step summary chip — left for the page to fill if needed */}
                </div>
                {isLastInput ? (
                  <button
                    type="button"
                    className="btn dark"
                    onClick={doSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Sending…' : 'Send'}
                    <span className="arrow">→</span>
                  </button>
                ) : (
                  <button type="button" className="btn dark" onClick={next}>
                    Continue
                    <span className="arrow">→</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  function ConfirmCard({ data, confirmMeta }) {
    const meta = (typeof confirmMeta === 'function') ? (confirmMeta(data) || []) : [];
    return (
      <div className="confirm">
        <div className="check-circle">✓</div>
        <h3 className="confirm-title">We'll be in touch within one business day.</h3>
        <p className="confirm-body">
          A confirmation is on its way to <b>{data.email || 'your inbox'}</b>.
          Bring photos of your space if you didn't already attach any — it makes the call ten times more useful.
        </p>
        {meta.length > 0 && (
          <div className="confirm-meta">
            {meta.map((m, i) => (
              <div key={i}><span>{m.label}</span><b>{m.value}</b></div>
            ))}
          </div>
        )}
        <a className="btn dark" href="tel:4167088053">Call us now · 416 708 8053 <span className="arrow">↗</span></a>
      </div>
    );
  }

  // Strip File objects out of fields_data so JSON.stringify doesn't choke.
  function serializeFields(d) {
    const out = {};
    for (const k of Object.keys(d)) {
      const v = d[k];
      if (k === 'files') {
        out.files = (v || []).map((f) => ({ name: f.name, size: f.size, type: f.type }));
      } else if (k === 'fields_touched') {
        // Skip — sent separately.
        continue;
      } else if (v instanceof File) {
        continue;
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  // --- Postal-code helper, used by several step.validate() functions ----------
  function isCanadianPostalCode(s) {
    const pc = String(s || '').trim().toUpperCase().replace(/\s/g, '');
    return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(pc);
  }

  // --- Expose to global ------------------------------------------------------
  const exports = {
    WizardForm,
    FileUploadField,
    CommentUploadsStep,
    getCommentUploadsStep,
    StepPostal,
    StepWhen,
    StepContact,
    validatePostal,
    validateWhen,
    validateContact,
    submitLead,
    useFormSession,
    isCanadianPostalCode,
    formatBytes,
    FORM_MAX_BYTES,
    FORM_MAX_FILES,
    FORM_ALLOWED_EXT,
  };
  // Expose to both window (browser) and globalThis (Node vm sandbox during
  // prerender). In the browser, window === globalThis so the second assign
  // is a noop but harmless.
  Object.assign(window, exports);
  if (typeof globalThis !== 'undefined') Object.assign(globalThis, exports);
})();


/* === saunas-page.jsx === */
// Northern Leisure Saunas detail page.
// Content sourced from solara-content.docx Part 2 (research) + Part 4 (page architecture).
// Design language reuses tokens, nav, marquee, form chrome, and footer from the main page,
// plus the .p-* component classes already defined for the pergolas detail page.

const { useState, useEffect, useRef, useMemo } = React;

// --------- Floating pill nav ---------
function Nav({ onDark }) {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const close = () => setMenuOpen(false);
  const goBook = (e) => {
    e.preventDefault();
    close();
    document.getElementById('book')?.scrollIntoView({behavior:'smooth', block:'start'});
  };

  return (
    <>
      <div className={`nav ${onDark ? "on-dark" : ""}`}>
        <div className="nav-pill">
          <a href="Solara Living.html" className="nav-brand">
            <img className="mark" src="images/solara-logo.svg" alt="" aria-hidden="true" />
            <span>SOLARA <b>LIVING</b></span>
          </a>
          <a href="Solara Living.html" className="nav-link">Home</a>
          <a href="about.html" className="nav-link">About</a>
          <div className="nav-dropdown nav-link" style={{padding:0}}>
            <a href="#" className="nav-link" style={{display:"inline-flex", alignItems:"center", gap:6}}>
              Products
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
            <div className="nav-dropdown-menu">
              <a href="pergolas.html">Pergolas</a>
              <a href="saunas.html"><b>Saunas</b></a>
              <a href="hot-tubs.html">Hot tubs</a>
              <a href="swim-spas.html">Swim spas</a>
            </div>
          </div>
          <a href="contact.html" className="nav-link">Contact</a>
          <button
            type="button"
            className="nav-hamburger"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(o => !o)}
          >
            {menuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            )}
          </button>
          <a href="#book" className="nav-cta" onClick={goBook}>Request a quote</a>
        </div>
      </div>

      <div
        className={`nav-drawer-backdrop ${menuOpen ? "open" : ""}`}
        onClick={close}
        aria-hidden="true"
      />
      <div className={`nav-drawer ${menuOpen ? "open" : ""}`} role="dialog" aria-modal="true" aria-label="Site menu">
        <div className="nav-drawer-section">Navigate</div>
        <a href="Solara Living.html" className="nav-drawer-link" onClick={close}>Home <span className="arrow">→</span></a>
        <a href="about.html" className="nav-drawer-link" onClick={close}>About <span className="arrow">→</span></a>
        <a href="contact.html" className="nav-drawer-link" onClick={close}>Contact <span className="arrow">→</span></a>
        <div className="nav-drawer-section">Products</div>
        <a href="pergolas.html" className="nav-drawer-link" onClick={close}>Pergolas <span className="arrow">→</span></a>
        <a href="saunas.html" className="nav-drawer-link active" onClick={close}>Saunas <span className="arrow">→</span></a>
        <a href="hot-tubs.html" className="nav-drawer-link" onClick={close}>Hot tubs <span className="arrow">→</span></a>
        <a href="swim-spas.html" className="nav-drawer-link" onClick={close}>Swim spas <span className="arrow">→</span></a>
      </div>
    </>
  );
}

// --------- Hero ---------
function Hero(){
  const headlineRef = useRef(null);
  const subRef = useRef(null);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = window.scrollY;
        const fade = Math.max(0, 1 - y / 480);
        const lift = (1 - fade) * 30;
        if (headlineRef.current){
          headlineRef.current.style.opacity = fade;
          headlineRef.current.style.transform = `translateY(${-lift}px)`;
        }
        if (subRef.current){
          subRef.current.style.opacity = fade;
          subRef.current.style.transform = `translateY(${-lift}px)`;
        }
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {passive:true});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const heroHeadingRaw = _C('saunas.hero.heading', 'Canadian Red Cedar,\nprecision-milled.');
  const heroHeadingHtml = String(heroHeadingRaw).replace(/\n/g, '<br/>');
  const heroSub = _C('saunas.hero.subheading', 'Five sauna lines from Northern Leisure: barrel, cube, classic cabin, modern cabin, and panoramic. Canadian Red Cedar throughout. Electric wet/dry heater, heat-retaining rocks, and Wi-Fi remote activation come standard. 6 and 8 foot footprints, with an optional 2 foot porch.');
  const heroSrc = _C('saunas.hero.src', 'images/gallery/sauna-cabin-forest.jpg');
  const heroAlt = _C('saunas.hero.alt', 'Black-clad cabin sauna with cedar interior glow set on a stone path through a moody pine forest');
  const primaryBtn = _C('saunas.hero.primaryButtonText', 'Request a quote');
  const secondaryBtn = _C('saunas.hero.secondaryButtonText', 'Explore the lines');
  const scrollHint = _C('saunas.scrollHint', 'scroll · five lines · 17 configurations');
  const breadcrumb = _C('saunas.breadcrumbLeaf', 'Saunas');

  return (
    <section className="p-hero">
      <div className="p-hero-photo">
        <img src={heroSrc} alt={heroAlt} />
      </div>
      <div className="p-hero-veil" />
      <div className="wrap p-hero-inner">
        <div className="p-breadcrumb">
          <a href="Solara Living.html"><span className="arrow">←</span>Solara Living</a>
          <span style={{margin:"0 12px", color:"rgba(247,242,234,.4)"}}>/</span>
          <span style={{color:"rgba(247,242,234,.55)"}}>Products</span>
          <span style={{margin:"0 12px", color:"rgba(247,242,234,.4)"}}>/</span>
          <span style={{color:"#F7F2EA"}}>{breadcrumb}</span>
        </div>
        <div className="p-hero-copy">
          <h1 ref={headlineRef} className="p-hero-headline display"
            dangerouslySetInnerHTML={{__html: heroHeadingHtml}}
          />
          <div ref={subRef} className="p-hero-sub-block">
            <p className="p-hero-sub">{heroSub}</p>
            <div className="p-hero-actions">
              <a className="btn" href="#book" onClick={(e)=>{e.preventDefault(); document.getElementById('book')?.scrollIntoView({behavior:'smooth', block:'start'});}}>{primaryBtn} <span className="arrow">↗</span></a>
              <a className="btn ghost" href="#types" onClick={(e)=>{e.preventDefault(); document.getElementById('types')?.scrollIntoView({behavior:'smooth', block:'start'});}}>{secondaryBtn} <span className="arrow">→</span></a>
            </div>
          </div>
        </div>
      </div>
      <div className="p-scroll-hint">{scrollHint}</div>
    </section>
  );
}

// --------- Marquee strip ---------
function MarqueeStrip(){
  const items = _CA('saunas.marqueeItems', [
    "Canadian Red Cedar throughout",
    "17 configurations across five lines",
    "Wi-Fi heater, standard",
    "Designed in Canada",
    "Wet or dry, your choice each session",
    "Dealer-delivered and installed",
  ]);
  const all = [...items, ...items, ...items];
  return (
    <div className="note-strip">
      <div className="marquee">
        {all.map((t,i)=>(<span key={i}><b>·</b>&nbsp; {t}</span>))}
      </div>
    </div>
  );
}

// --------- Reveal hook ---------
function useReveal(){
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); });
    }, { threshold: 0.18 });
    el.querySelectorAll('[data-reveal]').forEach(n => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
}

// --------- Five sauna types ---------
function Types(){
  const ref = useReveal();
  const fallback = [
    {tag: "Barrel",        title: "Where simple heating meets cedar beauty.",  tagline: "Efficient heating and even warmth in the classic Finnish silhouette. Handcrafted from kiln-dried Canadian Red Cedar.", priceFrom: "From $9,997", src: "images/products/sauna-barrel.jpg"},
    {tag: "Cube",          title: "Modern yet timeless.",                       tagline: "Sleek, square design that maximizes interior space while keeping a compact footprint. Built for smaller lots and urban yards.", priceFrom: "From $11,997", src: "images/products/sauna-cube.jpg"},
    {tag: "Classic Cabin", title: "Disguise your health hideaway.",             tagline: "Elegant cedar exterior and traditional cabin shape that blends into the landscape. The discreet pick for HOA lots and yards that don't want to announce themselves.", priceFrom: "From $12,997", src: "images/products/sauna-classic-cabin.jpg"},
    {tag: "Modern Cabin",  title: "Craftsmanship meets contemporary design.",   tagline: "Expansive windows bring in natural light and frame the view. Zinc-coated exterior, premium cedar interior, architecture-forward lines.", priceFrom: "From $17,997", src: "images/products/sauna-modern-cabin.jpg"},
    {tag: "Panoramic",     title: "Relaxation with a view.",                    tagline: "Expansive glass panels connect you to your surroundings and fill the interior with light. Built for backyards designed around a treeline, a lake, or a ravine.", priceFrom: "From $11,997", src: "images/products/sauna-panoramic.jpg"},
  ];
  const types = _CA('saunas.lineup', fallback);
  const eyebrow = _C('saunas.lineupEyebrow', 'Five lines');
  const headingRaw = _C('saunas.lineupHeading', 'Pick the silhouette.\n17 configurations follow.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const body = _C('saunas.lineupBody', 'Five distinct sauna profiles from Northern Leisure. Each line ships in 6 ft and 8 ft footprints, with an optional 2 ft porch on most.');
  const ctaLabel = _C('saunas.lineupCtaLabel', 'Book a consultation to pick the right one');
  const footnote = _C('saunas.lineupFootnote', "17 configurations across the five lines. We'll match silhouette, size, and porch on the call.");

  return (
    <section className="p-types" id="types" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2" dangerouslySetInnerHTML={{__html: headingHtml}} />
          <p className="body-lg">{body}</p>
        </div>
        <div className="p-types-grid">
          {types.map((t, i) => (
            <div className="p-type" key={t._key || t.tag} data-reveal style={{transitionDelay: `${i * 60}ms`}}>
              <div className="p-type-photo">
                <img src={t.src} alt={t.alt || `${t.tag} sauna by Northern Leisure`} loading="lazy" />
              </div>
              <div className="p-type-body">
                <div className="p-type-tag">{t.tag}</div>
                <h3>{t.title}</h3>
                <p>{t.tagline || t.body}</p>
                <div className="p-type-pricing">{t.priceFrom}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{textAlign:"center", marginTop: 56}}>
          <a
            className="btn dark"
            href="#book"
            onClick={(e)=>{e.preventDefault(); document.getElementById('book')?.scrollIntoView({behavior:'smooth', block:'start'});}}
          >
            {ctaLabel} <span className="arrow">↗</span>
          </a>
          <p className="small" style={{marginTop: 14}}>{footnote}</p>
        </div>
      </div>
    </section>
  );
}

// --------- Sizing comparison: 6 ft vs 8 ft + optional porch ---------
function Sizing(){
  const ref = useReveal();
  const sizes = [
    {
      label: "6 ft",
      tag: "Compact",
      rows: [
        ["Capacity", "2 to 3 people"],
        ["Heat-up time", "About 30 minutes"],
        ["Power draw", "240V / 30A, 6 kW heater"],
        ["Best for", "Couples, single bathers, tighter footprints"],
      ],
    },
    {
      label: "8 ft",
      tag: "Standard",
      rows: [
        ["Capacity", "3 to 4 people"],
        ["Heat-up time", "About 40 minutes"],
        ["Power draw", "240V / 30A, 6 kW heater"],
        ["Best for", "Families, lounging, the full traditional rhythm"],
      ],
    },
  ];
  // Pull from CMS — schema returns array of {label, tag, unit, rows: [{label, value}]}.
  // Fallback uses the legacy {label, tag, rows: [[k,v]]} shape; we normalize below.
  const cmsCards = _CA('saunas.sizingCards', null);
  const cards = cmsCards
    ? cmsCards.map((c) => ({label: c.label, tag: c.tag, unit: c.unit || 'footprint', rows: (c.rows || []).map((r) => [r.label, r.value])}))
    : sizes.map((s) => ({...s, unit: 'footprint'}));
  const eyebrow = _C('saunas.sizingEyebrow', 'Sizing');
  const headingRaw = _C('saunas.sizingHeading', '6 ft or 8 ft.\nAdd a porch if you want one.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const body = _C('saunas.sizingBody', 'Every sauna line is offered in two footprints. Most lines also offer a 2 ft porch extension that gives you a covered cool-down spot before you walk back inside.');
  const porchCallout = _C('saunas.porchCallout', 'Optional 2 ft porch. A covered cool-down step at the entry, available on most barrel, cube, and modern lines. Adds about $2,000 over the base configuration.');

  return (
    <section className="p-sizing" id="sizing" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2" dangerouslySetInnerHTML={{__html: headingHtml}} />
          <p className="body-lg">{body}</p>
        </div>
        <div className="p-sizes-grid">
          {cards.map((s, i) => (
            <div className="p-size" key={s.label} data-reveal style={{transitionDelay: `${i * 80}ms`}}>
              <div className="p-size-tag">{s.tag}</div>
              <div className="p-size-h">{s.label}<span className="unit">{s.unit || 'footprint'}</span></div>
              {(s.rows || []).map(([k, v], ri) => (
                <div className="p-size-row" key={k || ri}>
                  <span>{k}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="p-porch-callout" data-reveal>
          <div className="plus">+</div>
          <div className="copy">{porchCallout}</div>
        </div>
      </div>
    </section>
  );
}

// --------- Specs strip ---------
function Specs(){
  const ref = useReveal();
  const fallback = [
    { num:"Cedar",  unit:"Canadian Red", body:"Precision-milled Canadian Red Cedar throughout. Naturally rot-resistant, dimensionally stable, and aromatic for years." },
    { num:"6 kW",   unit:"Wi-Fi heater", body:"Electric wet/dry heater with heat-retaining rocks. Pre-heat from your phone so the sauna is ready when you walk out." },
    { num:"Wet/Dry",unit:"any session",  body:"Pour water on the rocks for a steam-rich Finnish-style löyly, or run it dry. You choose each session, not at install." },
    { num:"Steel",  unit:"weatherproof", body:"Waterproof roof membrane with steel covering. Built to take Canadian winters, falling snow, and four-season use." },
  ];
  const specs = _CA('saunas.specs', fallback);
  const eyebrow = _C('saunas.specsEyebrow', 'Built-in standard');
  const heading = _C('saunas.specsHeading', 'What you get out of the box.');
  const body = _C('saunas.specsBody', 'Every Northern Leisure sauna ships with the same core. The differences across the five lines are silhouette, size, and finishing details. The hardware is the same.');
  return (
    <section className="p-specs" id="specs" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2">{heading}</h2>
          <p className="body-lg">{body}</p>
        </div>
        <div className="p-specs-grid">
          {specs.map((s, i) => (
            <div className="p-spec" key={s._key || s.num || i} data-reveal style={{transitionDelay: `${i * 80}ms`}}>
              <div className="p-spec-num">{String(i + 1).padStart(2, '0')}</div>
              <div className="p-spec-h">{s.num || s.h}<span className="unit">{s.unit}</span></div>
              <p className="p-spec-l">{s.body || s.l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --------- FAQ ---------
function FAQ(){
  const ref = useReveal();
  const [open, setOpen] = useState(0);
  const faqs = [
    {
      q: "How much does it cost to run?",
      a: "The 6 kW heater pulls 6 kWh per hour of operation. A heat-up plus a typical 60 to 90 minute session uses 4 to 7 kWh, which works out to roughly 60 cents to a dollar per session at Ontario electricity rates. Run a sauna two or three times a week and the monthly cost is in the $15 to $25 range, well below most hot tubs.",
    },
    {
      q: "What does installation cover?",
      a: "Northern Leisure dealers handle delivery to your property and place the sauna on a prepared base. The customer arranges a certified electrician for the actual hookup, since codes and permit requirements vary by municipality. We confirm site, foundation, and electrical specs during the consultation so your electrician has everything in advance.",
    },
    {
      q: "What electrical do I need?",
      a: "The 6 kW heater runs on 240V, 30A, hard-wired through a dedicated circuit and disconnect. A licensed electrician installs the run from your panel. The Wi-Fi remote activation comes online once the wiring is in. We send the spec sheet ahead of time so quoting the electrical is fast.",
    },
    {
      q: "Can I put a sauna on my existing deck?",
      a: "Sometimes. A 6 ft barrel sauna weighs around 800 to 1,000 lbs once people are inside. 8 ft cabin saunas are heavier. Most existing residential decks are not built for that point load. We review your deck framing during the consultation. If the deck cannot carry the load, a simple sleeper system or concrete pad is the usual fix.",
    },
    {
      q: "How long is a typical session?",
      a: "15 to 20 minutes per round, with a 5 to 10 minute cool-down between rounds. Two or three rounds is the traditional rhythm. The first round warms the body. Heat retention from the rocks keeps the temperature steady through the cycle even with the door opening between rounds.",
    },
    {
      q: "Dry sauna or steam?",
      a: "Both, in the same unit. Northern Leisure heaters are wet/dry. Run it dry for traditional Finnish-style heat, or pour water over the heat-retaining rocks for a steam-rich löyly. You decide each session, not at install. There is no separate steam configuration to choose.",
    },
    {
      q: "How do I maintain it?",
      a: "Wipe down the benches after each session. Sweep the floor periodically. Re-treat the cedar exterior every 2 to 3 years if you want to preserve color, or let it weather to a silver-gray patina, which many owners prefer. Replace the heater rocks if they crack, typically every few years of regular use.",
    },
  ];
  const cmsFaqs = _CA('saunas.faqItems', null);
  const items = cmsFaqs ? cmsFaqs.map((f) => ({q: f.question || f.q, a: f.answer || f.a})) : faqs;
  const faqEyebrow = _C('saunas.faqEyebrow', 'Common questions');
  const faqHeadingRaw = _C('saunas.faqHeading', 'Everything we\nget asked first.');
  const faqHeadingHtml = String(faqHeadingRaw).replace(/\n/g, '<br/>');
  return (
    <section className="p-faq-section" id="faq" ref={ref}>
      <div className="wrap">
        <div className="p-faq-grid">
          <div data-reveal>
            <span className="eyebrow" style={{display:"block", marginBottom:18}}>{faqEyebrow}</span>
            <h2 className="h2" style={{margin:"0 0 18px 0", fontSize:"clamp(36px, 4.6vw, 64px)"}}
              dangerouslySetInnerHTML={{__html: faqHeadingHtml}}
            />
            <p className="body-lg" style={{margin:0, maxWidth:"36ch"}}>
              {items.length} questions covered. If yours is not here, we'll answer it on the consultation.
            </p>
          </div>
          <div className="p-faq-list" data-reveal>
            {items.map((f, i) => (
              <div key={i} className={`p-faq-item ${open === i ? "open" : ""}`}>
                <button type="button" className="p-faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                  <span>{f.q}</span>
                  <span className="pm">+</span>
                </button>
                <div className="p-faq-a">
                  <div className="p-faq-a-inner">{f.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// --------- Sauna quote form (wizard mechanics live in solara-form.jsx) ---------

const SAUNA_LINE_OPTS = [
  { v: 'Barrel',        note: 'Classic Finnish silhouette. Cedar staves rolled into a cylinder.' },
  { v: 'Cube',          note: 'Boxy, modern footprint with more usable wall space.' },
  { v: 'Classic Cabin', note: 'Pitched-roof traditional cabin, single door.' },
  { v: 'Modern Cabin',  note: 'Architecture-forward, sleek lines, often black-clad.' },
  { v: 'Panoramic',     note: 'Built around an oversized window. Designed for views.' },
  { v: 'Not sure yet',  note: "We'll walk you through all five on the call." },
];

const SAUNA_BUDGET_OPTS = [
  { v: 'Essential',    range: '$9,997 to $11,997',  note: '6 ft and 8 ft Barrel, 6 ft Cube, 6 ft Panoramic Barrel, 6 ft Barrel with porch.' },
  { v: 'Plus',         range: '$12,997 to $14,997', note: '8 ft Barrel with porch, 8 ft Cube, 8 ft Classic Cabin, 6 ft Mini Modern Panoramic.' },
  { v: 'Modern',       range: '$15,997 to $19,997', note: '8 ft Modern Cabin, 8 ft Modern Panoramic, 8 ft Panoramic with porch.' },
  { v: 'Not sure yet', range: '',                   note: "Skips bracket selection. We'll talk it through on the call." },
];

function StepSaunaLine({ data, update, errors }) {
  return (
    <div className="opts">
      {SAUNA_LINE_OPTS.map((o) => (
        <button
          key={o.v}
          type="button"
          className={`opt ${data.line === o.v ? 'sel' : ''}`}
          onClick={() => update('line', o.v)}
        >
          <span className="opt-meta">
            <b>{o.v}</b>
            <span>{o.note}</span>
          </span>
          <span className="dot" />
        </button>
      ))}
      {errors.line && <div className="err-msg">{errors.line}</div>}
    </div>
  );
}

function StepSaunaBudget({ data, update, errors }) {
  return (
    <div className="opts">
      {SAUNA_BUDGET_OPTS.map((b) => (
        <button
          key={b.v}
          type="button"
          className={`opt ${data.budget === b.v ? 'sel' : ''}`}
          onClick={() => update('budget', b.v)}
        >
          <span className="opt-meta">
            <b>{b.v}{b.range && <span style={{marginLeft: 10, color: 'var(--muted)', fontWeight: 400}}>{b.range}</span>}</b>
            <span>{b.note}</span>
          </span>
          <span className="dot" />
        </button>
      ))}
      {errors.budget && <div className="err-msg">{errors.budget}</div>}
    </div>
  );
}

function SaunaForm(){
  const steps = useMemo(() => [
    {
      id: 'where', title: 'Where is it going?',
      sub: 'Postal code helps us check delivery and electrical.',
      render: StepPostal, validate: validatePostal,
    },
    {
      id: 'line', title: 'Which line are you drawn to?',
      sub: 'Pick a starting point. We can change your mind on the call.',
      render: StepSaunaLine,
      validate: (d) => d.line ? {} : { line: "Pick a line or 'Not sure yet'." },
    },
    {
      id: 'when',  title: 'When are you hoping to install?',
      sub: 'Most sauna lines are stocked. 4 to 8 weeks from order to install is typical.',
      render: StepWhen, validate: validateWhen,
    },
    {
      id: 'budget', title: 'Budget bracket?',
      sub: 'Sauna-only. Delivery, electrical, and base prep are quoted separately.',
      render: StepSaunaBudget,
      validate: (d) => d.budget ? {} : { budget: 'Pick a budget bracket.' },
    },
    {
      id: 'contact', title: 'How can we reach you?',
      sub: 'Just the basics. We never share or sell.',
      render: StepContact, validate: validateContact,
    },
    getCommentUploadsStep(),
  ], []);
  return (
    <WizardForm
      formId="saunas"
      sectionId="book"
      eyebrow="Sauna consultation"
      title="Two minutes. No pressure."
      body="Six quick questions and we'll be in touch within one business day. Bring photos of your space if you have them. It makes the call ten times more useful."
      initialData={{
        postal: '', venue: 'Residential', line: '', when: '', budget: '',
        name: '', email: '', phone: '', contactPref: 'Phone',
      }}
      steps={steps}
      productInterest="sauna"
      confirmMeta={(d) => [
        { label: 'Product', value: 'Sauna' },
        { label: 'Line',    value: d.line || 'TBD' },
        { label: 'Postal',  value: String(d.postal || '').toUpperCase() || '—' },
        { label: 'Budget',  value: d.budget || '—' },
      ]}
    />
  );
}

// --------- Gallery: CSS Grid bento + FLIP animation on expand ---------
function Gallery(){
  const gridRef = useRef(null);
  const [openIdx, setOpenIdx] = useState(null);

  const items = [
    { src:"images/gallery/sauna-cabin-forest.jpg",  caption:"Cabin · forest",         size:"feature" },
    { src:"images/sauna-main-page.jpg",             caption:"Cabin · backyard path",  size:"default" },
    { src:"images/gallery/sauna-garden.jpg",        caption:"Barrel · garden",        size:"default" },
    { src:"images/gallery/sauna-interior-view.jpg", caption:"Interior · lake view",   size:"default" },
    { src:"images/gallery/sauna-couple-steam.jpg",  caption:"Cedar interior · steam", size:"default" },
    { src:"images/gallery/sauna-cedar-detail.jpg",  caption:"Cedar · pine border",    size:"default" },
  ];

  const flipTo = (newIdx) => {
    const grid = gridRef.current;
    if (!grid) { setOpenIdx(newIdx); return; }
    const tiles = Array.from(grid.querySelectorAll('.pg-tile'));
    const before = new Map();
    tiles.forEach(t => before.set(t, t.getBoundingClientRect()));
    setOpenIdx(newIdx);
    requestAnimationFrame(() => {
      // Capture new layout rects BEFORE animations start (animation transforms
      // would otherwise contaminate getBoundingClientRect readings).
      const newRects = new Map();
      tiles.forEach(t => newRects.set(t, t.getBoundingClientRect()));

      if (newIdx !== null) {
        const expanded = grid.querySelector('.pg-tile.is-expanded');
        const rect = expanded ? newRects.get(expanded) : null;
        if (rect) {
          const targetY = window.scrollY + rect.top - 100;
          window.scrollTo({ top: targetY, behavior: "smooth" });
        }
      }

      tiles.forEach(t => {
        const oldRect = before.get(t);
        const newRect = newRects.get(t);
        const dx = oldRect.left - newRect.left;
        const dy = oldRect.top  - newRect.top;
        const sx = oldRect.width  / Math.max(newRect.width,  1);
        const sy = oldRect.height / Math.max(newRect.height, 1);
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(sx - 1) < 0.01 && Math.abs(sy - 1) < 0.01) return;
        t.animate(
          [
            { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, transformOrigin: "0 0" },
            { transform: "translate(0, 0) scale(1, 1)",                       transformOrigin: "0 0" },
          ],
          { duration: 520, easing: "cubic-bezier(0.22, 0.85, 0.25, 1)", fill: "both" }
        );
      });
    });
  };

  const handleClick = (i) => {
    if (window.matchMedia('(max-width: 720px)').matches) return;
    flipTo(openIdx === i ? null : i);
  };

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => { if (e.key === "Escape") flipTo(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIdx]);

  return (
    <section className="pg-section">
      <div className="pg-head">
        <span className="eyebrow" style={{display:"block", marginBottom:18}}>Selected projects</span>
        <h2 className="h2" style={{margin:"0 0 18px 0", fontSize:"clamp(36px, 4.6vw, 64px)"}}>Northern Leisure saunas, in the wild.</h2>
        <p className="body-lg" style={{margin:0, maxWidth:"42ch"}}>
          Real installs across the lines. Click any photo to see it full size.
        </p>
      </div>

      <div className="pg-grid" ref={gridRef}>
        {items.map((it, i) => {
          const open = openIdx === i;
          return (
            <button
              type="button"
              key={i}
              className={`pg-tile size-${it.size} ${open ? "is-expanded" : ""}`}
              onClick={() => handleClick(i)}
              aria-label={open ? `Close ${it.caption}` : `Expand ${it.caption}`}
            >
              <img src={it.src} alt={it.caption} loading="lazy" />
              {it.caption && <div className="pg-cap">{it.caption}</div>}
              <span className="pg-glyph" aria-hidden="true">
                <svg className="ic-expand" viewBox="0 0 24 24"><path d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5"/></svg>
                <svg className="ic-close" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// --------- Closing track ---------
function Closing(){
  const ref = useReveal();
  const fallback = [
    { num:"01", heading:"17 configurations", body:"Five lines, two footprints, optional porches. The catalog is wide enough that one of these is the right match." },
    { num:"02", heading:"Designed in Canada", body:"Northern Leisure designs in Sudbury and Meaford, Ontario. Built for our climate, our power, and our winters." },
    { num:"03", heading:"Dealer delivered", body:"Solara handles delivery and placement on the prepared base. Your electrician finishes the hookup. We coordinate the whole thing." },
  ];
  const cards = _CA('saunas.closing', fallback);
  const eyebrow = _C('saunas.closingEyebrow', 'Why this brand');
  const headingRaw = _C('saunas.closingHeading', "A wider catalog\nthan you'd expect.");
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  return (
    <section className="p-closing" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2" dangerouslySetInnerHTML={{__html: headingHtml}} />
        </div>
        <div className="p-closing-grid">
          {cards.map((c, i) => (
            <div className="p-close-card" key={c._key || c.num || i} data-reveal style={{transitionDelay: `${i * 80}ms`}}>
              <div className="num">{c.num}</div>
              <h3>{c.heading || c.h}</h3>
              <p>{c.body || c.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --------- Final CTA ---------
function FinalCTA(){
  const eyebrow = _C('saunas.cta.eyebrow', '· Northern Leisure Saunas · GTA ·');
  const headingRaw = _C('saunas.cta.heading', 'Your backyard,\nfinished.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const sub = _C('saunas.cta.subheading', "Book a 30-minute consultation. We'll walk your space, talk lines, and send a fixed quote within 48 hours.");
  const buttonText = _C('saunas.cta.buttonText', 'Request a quote');
  const buttonLink = _C('saunas.cta.buttonLink', '#book');
  const phone = _C('site.phone', '416 708 8053');
  return (
    <section className="final-cta">
      <div className="wrap final-inner">
        <div className="final-eyebrow">{eyebrow}</div>
        <h2 className="final-headline display" dangerouslySetInnerHTML={{__html: headingHtml}} />
        <p className="final-sub">{sub}</p>
        <div className="final-actions">
          <a className="btn dark" href={buttonLink} onClick={(e)=>{if (buttonLink === '#book') {e.preventDefault(); document.getElementById('book')?.scrollIntoView({behavior:'smooth', block:'start'});}}}>{buttonText} <span className="arrow">↗</span></a>
          <a className="btn ghost-dark" href={`tel:${String(phone).replace(/\D/g, '')}`}>{phone} <span className="arrow">→</span></a>
        </div>
      </div>
    </section>
  );
}

// --------- Footer ---------
function Footer(){
  const tagline = _C('site.footerCopy', 'Premium outdoor living. Designed, delivered, and installed across the GTA and southern Ontario.');
  const phone = _C('site.phone', '416 708 8053');
  const email = _C('site.salesEmail', 'hello@solaraliving.ca');
  return (
    <footer className="footer">
      <div className="wrap footer-inner">
        <div className="footer-brand">
          <div className="wordmark">
            <img className="mark" src="images/solara-logo.svg" alt="" aria-hidden="true" />
            <span>SOLARA <b>LIVING</b></span>
          </div>
          <div className="small">{tagline}</div>
        </div>
        <div className="footer-cols">
          <div>
            <div className="col-h">Products</div>
            <a href="pergolas.html">Pergolas</a>
            <a href="saunas.html">Saunas</a>
            <a href="hot-tubs.html">Hot tubs</a>
            <a href="swim-spas.html">Swim spas</a>
          </div>
          <div>
            <div className="col-h">Company</div>
            <a href="Solara Living.html">Home</a>
            <a href="Solara Living.html#commercial">Commercial</a>
            <a href="Solara Living.html#gallery">Projects</a>
          </div>
          <div>
            <div className="col-h">Get in touch</div>
            <a href={`tel:${String(phone).replace(/\D/g, '')}`}>{phone}</a>
            <a href={`mailto:${email}`}>{email}</a>
            <a href="#book">Book a consultation</a>
          </div>
        </div>
      </div>
      <div className="wrap footer-base">
        <span>© {new Date().getFullYear()} Solara Living Inc.</span>
        <span>Lifetime warranty · Designed in Canada</span>
      </div>
    </footer>
  );
}

// --------- App ---------
function App(){
  const [navDark, setNavDark] = useState(true);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const heroH = window.innerHeight * 0.92;
        setNavDark(window.scrollY < heroH - 80);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {passive:true});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <Nav onDark={navDark} />
      <Hero />
      <MarqueeStrip />
      <Types />
      <Sizing />
      <Specs />
      <FAQ />
      <SaunaForm />
      <Gallery />
      <Closing />
      <FinalCTA />
      <Footer />
    </>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
